import { ReactNode } from 'react';
import Card from '~/components/Card';
import Page from '~/components/layout/page/Page';

function ColorGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card headline={title}>
      <div className="grid grid-cols-[2fr_1fr] gap-4">{children}</div>
    </Card>
  );
}

function Swatch({
  bg,
  text,
  label,
}: {
  bg: string;
  text: string;
  label: string;
}) {
  return (
    <div className={`${bg} ${text} p-2`}>
      <span className="body-sm">{label}</span>
    </div>
  );
}
type Role =
  | 'brand'
  | 'brand-container'
  | 'plain'
  | 'plain-container'
  | 'emphasis'
  | 'emphasis-container'
  | 'critical'
  | 'critical-container';
const roleStyles: Record<
  Role,
  { bg: string; text: string; onBg: string; onText: string }
> = {
  brand: {
    bg: 'bg-brand',
    text: 'text-on-brand',
    onBg: 'bg-on-brand',
    onText: 'text-brand',
  },
  'brand-container': {
    bg: 'bg-brand-container',
    text: 'text-on-brand-container',
    onBg: 'bg-on-brand-container',
    onText: 'text-brand-container',
  },
  plain: {
    bg: 'bg-plain',
    text: 'text-on-plain',
    onBg: 'bg-on-plain',
    onText: 'text-plain',
  },
  'plain-container': {
    bg: 'bg-plain-container',
    text: 'text-on-plain-container',
    onBg: 'bg-on-plain-container',
    onText: 'text-plain-container',
  },
  emphasis: {
    bg: 'bg-emphasis',
    text: 'text-on-emphasis',
    onBg: 'bg-on-emphasis',
    onText: 'text-emphasis',
  },
  'emphasis-container': {
    bg: 'bg-emphasis-container',
    text: 'text-on-emphasis-container',
    onBg: 'bg-on-emphasis-container',
    onText: 'text-emphasis-container',
  },
  critical: {
    bg: 'bg-critical',
    text: 'text-on-critical',
    onBg: 'bg-on-critical',
    onText: 'text-critical',
  },
  'critical-container': {
    bg: 'bg-critical-container',
    text: 'text-on-critical-container',
    onBg: 'bg-on-critical-container',
    onText: 'text-critical-container',
  },
} as const;
function SwatchGroup({ colorRole: role }: { colorRole: Role }) {
  const styles = roleStyles[role];
  return (
    <div>
      <Swatch bg={styles.bg} text={styles.text} label={role} />
      <Swatch bg={styles.onBg} text={styles.onText} label={`on-${role}`} />
    </div>
  );
}

export default function ColorsPage() {
  return (
    <Page.Root title="Colors">
      <Page.Section title="Palette">
        <div className="grid grid-cols-4 gap-x-1 gap-y-2 bg-surface-container">
          <SwatchGroup colorRole="brand" />
          <SwatchGroup colorRole="plain" />
          <SwatchGroup colorRole="emphasis" />
          <SwatchGroup colorRole="critical" />
          <SwatchGroup colorRole="brand-container" />
          <SwatchGroup colorRole="plain-container" />
          <SwatchGroup colorRole="emphasis-container" />
          <SwatchGroup colorRole="critical-container" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-x-1 gap-y-2 bg-surface-container">
          <Swatch bg="bg-surface" text="text-on-surface" label="surface" />
          <Swatch
            bg="bg-surface-container-lowest"
            text="text-on-surface"
            label="surface-container-lowest"
          />
          <Swatch
            bg="bg-surface-container-low"
            text="text-on-surface"
            label="surface-container-low"
          />
          <Swatch
            bg="bg-surface-container"
            text="text-on-surface"
            label="surface-container"
          />
          <Swatch
            bg="bg-surface-container-high"
            text="text-on-surface"
            label="surface-container-high"
          />
          <Swatch
            bg="bg-surface-container-highest"
            text="text-on-surface"
            label="surface-container-highest"
          />
          <Swatch bg="bg-on-surface" text="text-surface" label="on-surface" />
          <Swatch
            bg="bg-on-surface-variant"
            text="text-surface"
            label="on-surface-variant"
          />
          <Swatch
            bg="bg-inverse-surface"
            text="text-inverse-on-surface"
            label="inverse-surface"
          />
          <Swatch
            bg="bg-inverse-on-surface"
            text="text-inverse-surface"
            label="inverse-on-surface"
          />
          <Swatch bg="bg-outline" text="text-on-surface" label="outline" />
          <Swatch
            bg="bg-outline-variant"
            text="text-on-surface"
            label="outline-variant"
          />
          <Swatch bg="bg-scrim" text="text-surface" label="scrim" />
        </div>
      </Page.Section>

      <ColorGroup title="Brand">
        <div>
          <p className="pb-4">
            Brand is used to pull additional focus on main actions in the UI.
            Use brand role color for the most prominent components across the
            UI, such as high-emphasis buttons and active states.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Brand:</strong> High-emphasis fills, texts, and icons
              against surface
            </li>
            <li>
              <strong>On Brand:</strong> Text and icons against brand
            </li>
            <li>
              <strong>Brand Container:</strong> Standout fill color against
              surface, for key components
            </li>
            <li>
              <strong>On Brand Container:</strong> Text and icons against brand
              container
            </li>
          </ul>
        </div>
        <div>
          <SwatchGroup colorRole="brand" />
          <SwatchGroup colorRole="brand-container" />
        </div>
      </ColorGroup>

      <ColorGroup title="Plain">
        <div>
          <p className="pb-4">
            The plain role is used as the baseline color role for all
            experiences. This role defines less prominent components used when
            communicating default statuses and neutral messaging in the UI.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Plain:</strong> Less prominent fills, text, and icons
              against surface
            </li>
            <li>
              <strong>On Plain:</strong> Text and icons against plain
            </li>
            <li>
              <strong>Plain Container:</strong> Less prominent fill color
              against surface, for recessive components like tonal buttons
            </li>
            <li>
              <strong>On Plain Container:</strong> Text and icons against plain
              container
            </li>
          </ul>
        </div>
        <div>
          <SwatchGroup colorRole="plain" />
          <SwatchGroup colorRole="plain-container" />
        </div>
      </ColorGroup>

      <ColorGroup title="Emphasis">
        <div>
          <p className="pb-4">
            The emphasis color role is used to indicate areas of focus within
            the UI and to highlight active elements in editors. Use emphasis
            roles for contrasting accents that balance brand and plain colors or
            bring heightened attention to an element such as a badge, input,
            etc.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Emphasis:</strong> Complementary fills, text, and icons
              against surface
            </li>
            <li>
              <strong>On Emphasis:</strong> Text and icons against emphasis
            </li>
            <li>
              <strong>Emphasis Container:</strong> Complementary container color
              against surface, for components like input fields
            </li>
            <li>
              <strong>On Emphasis Container:</strong> Text and icons against
              emphasis container
            </li>
          </ul>
        </div>
        <div>
          <SwatchGroup colorRole="emphasis" />
          <SwatchGroup colorRole="emphasis-container" />
        </div>
      </ColorGroup>

      <ColorGroup title="Critical">
        <div>
          <p className="pb-4">
            Critical is used as the color of highest importance. Elements using
            critical must convey messaging that implies that an action is
            impossible, blocked, or has resulted in an error.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Critical:</strong> Complementary fills, text, and icons
              against surface
            </li>
            <li>
              <strong>On Critical:</strong> Text and icons against critical
            </li>
            <li>
              <strong>Critical Container:</strong> Complementary container color
              against surface, for components like input fields
            </li>
            <li>
              <strong>On Critical Container:</strong> Text and icons against
              emphasis container
            </li>
          </ul>
        </div>
        <div>
          <SwatchGroup colorRole="critical" />
          <SwatchGroup colorRole="critical-container" />
        </div>
      </ColorGroup>

      <ColorGroup title="Surface">
        <div>
          <p className="pb-4">
            Use surface roles for more neutral backgrounds, and container colors
            for components like cards, sheets, and dialogs.
          </p>
          <p className="pb-2">There are three surface roles:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Surface:</strong> Default background color
            </li>
            <li>
              <strong>On Surface:</strong> Text and icons against any surface or
              surface container
            </li>
            <li>
              <strong>On Surface Variant:</strong> Lower-emphasis text and icons
              against any surface or surface container
            </li>
          </ul>
          <p className="pt-4 pb-2">There are five surface container roles:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Surface Container Lowest:</strong> Lowest-emphasis
              container color
            </li>
            <li>
              <strong>Surface Container Low:</strong> Low-emphasis container
              color
            </li>
            <li>
              <strong>Surface Container:</strong> Default container color
            </li>
            <li>
              <strong>Surface Container High:</strong> High-emphasis container
              color
            </li>
            <li>
              <strong>Surface Container Highest:</strong> Highest-emphasis
              container color
            </li>
          </ul>
          <p className="pt-4">Additional surface roles include:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Inverse Surface:</strong> Used for backgrounds to contrast
              against surface
            </li>
            <li>
              <strong>Inverse On Surface:</strong> Text and icons against
              inverse surface
            </li>
          </ul>
        </div>
        <div>
          <Swatch bg="bg-surface" text="text-on-surface" label="surface" />
          <Swatch
            bg="bg-surface-container-lowest"
            text="text-on-surface"
            label="surface-container-lowest"
          />
          <Swatch
            bg="bg-surface-container-low"
            text="text-on-surface"
            label="surface-container-low"
          />
          <Swatch
            bg="bg-surface-container"
            text="text-on-surface"
            label="surface-container"
          />
          <Swatch
            bg="bg-surface-container-high"
            text="text-on-surface"
            label="surface-container-high"
          />
          <Swatch
            bg="bg-surface-container-highest"
            text="text-on-surface"
            label="surface-container-highest"
          />
          <Swatch bg="bg-on-surface" text="text-surface" label="on-surface" />
          <Swatch
            bg="bg-on-surface-variant"
            text="text-surface"
            label="on-surface-variant"
          />

          <Swatch
            bg="bg-inverse-surface"
            text="text-inverse-on-surface"
            label="inverse-surface"
          />
          <Swatch
            bg="bg-inverse-on-surface"
            text="text-inverse-surface"
            label="inverse-on-surface"
          />
        </div>
      </ColorGroup>

      <ColorGroup title="Additional Colors">
        <div>
          <p className="pb-4">
            Additional colors used for outlines, scrims, and shadows throughout
            the app.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Outline:</strong> Used for important boundaries such as
              text field outlines
            </li>
            <li>
              <strong>Outline Variant:</strong> Decorative elements such as
              dividers
            </li>
            <li>
              <strong>Scrim:</strong> Used for overlays and backdrops
            </li>
          </ul>
        </div>
        <div>
          <Swatch bg="bg-outline" text="text-on-surface" label="outline" />
          <Swatch
            bg="bg-outline-variant"
            text="text-on-surface"
            label="outline-variant"
          />
          <Swatch bg="bg-scrim" text="text-surface" label="scrim" />
        </div>
      </ColorGroup>
    </Page.Root>
  );
}
